/**
 * Maps wired domain fetch instances to the public context value shape.
 * @module dataAccumulator/buildContextValue
 */
import { WritebackHistoryResult } from "../../api/services/candid/candidWritebackHistoryTypes";
import { DenialQueryBucketFetchPayload } from "../../api/services/denialQueryBucketService";
import { DenialQueryBucketSummaryResponse } from "../../data-model/denialQueryBucket";
import { SiblingOrdersResponse } from "../../data-model/siblingOrdersModel";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { FilterJCodeInput } from "../utils/filterSiblingOrdersBasedOnConditions";
import {
  DataAccumulatorContextValue,
  DomainFetchInstance,
  SingleOrderDomainInstances,
} from "./types";

/**
 * Inputs required to assemble the full data accumulator context value.
 */
interface BuildContextValueParams {
  /** All wired Postgres domain fetch instances. */
  domains: SingleOrderDomainInstances;
  /** Denial/query bucket service fetch instance. */
  denialQueryBucket: DomainFetchInstance<DenialQueryBucketSummaryResponse>;
  /** Candid write-back history service fetch instance. */
  candidHistoryWriteback: DomainFetchInstance<WritebackHistoryResult>;
  /** Filtered sibling orders derived from the filtering orchestrator. */
  filteredSiblingOrders: SiblingOrdersResponse | null;
  /** Callback to recompute filtered sibling orders from parent J-codes. */
  recomputeFilteredSiblingOrders: (parentJCodes: FilterJCodeInput[]) => void;
  /** Combined loading flag across all domain fetches, including sibling orders. */
  loading: boolean;
  /** Triggers all domain fetches in parallel. */
  fetchAll: DataAccumulatorContextValue["fetchAll"];
  /** Aborts every in-flight domain request. */
  abortAll: DataAccumulatorContextValue["abortAll"];
  /** Clears cached data and selection state for a new worklist visit. */
  resetAll: DataAccumulatorContextValue["resetAll"];
  /** Linked sibling order ids selected in expandable linked-order tables. */
  selectedSiblingOrderIds: string[];
  /** Replaces the full selected sibling order id list. */
  setSelectedSiblingOrderIds: DataAccumulatorContextValue["setSelectedSiblingOrderIds"];
  /** When true, sibling-order checkboxes cannot be toggled. */
  isSiblingOrderSelectionDisabled: boolean;
  /** Enables or disables sibling-order checkbox interaction. */
  setIsSiblingOrderSelectionDisabled: DataAccumulatorContextValue["setIsSiblingOrderSelectionDisabled"];
}

/**
 * Builds the typed context value exposed by {@link DataAccumulatorForSingleOrderProvider}.
 *
 * @param params - {@link BuildContextValueParams}
 * @returns The complete {@link DataAccumulatorContextValue}
 */
export const buildContextValue = ({
  domains,
  denialQueryBucket,
  candidHistoryWriteback,
  filteredSiblingOrders,
  recomputeFilteredSiblingOrders,
  loading,
  fetchAll,
  abortAll,
  resetAll,
  selectedSiblingOrderIds,
  setSelectedSiblingOrderIds,
  isSiblingOrderSelectionDisabled,
  setIsSiblingOrderSelectionDisabled,
}: BuildContextValueParams): DataAccumulatorContextValue => ({
  generalOrderDetails: domains.generalOrderDetails.data,
  generalOrderDetailsLoading: domains.generalOrderDetails.loading,
  generalOrderDetailsError: domains.generalOrderDetails.error,
  fetchGeneralOrderDetails: domains.generalOrderDetails.runFetch,

  authOnFileDetails: domains.authOnFileDetails.data,
  authOnFileDetailsLoading: domains.authOnFileDetails.loading,
  authOnFileDetailsError: domains.authOnFileDetails.error,
  fetchAuthOnFileDetails: domains.authOnFileDetails.runFetch,

  clinicalData: domains.clinicalData.data,
  clinicalDataLoading: domains.clinicalData.loading,
  clinicalDataError: domains.clinicalData.error,
  fetchClinicalData: domains.clinicalData.runFetch,

  commentsData: domains.commentsData.data,
  commentsDataLoading: domains.commentsData.loading,
  commentsDataError: domains.commentsData.error,
  fetchCommentsData: domains.commentsData.runFetch,

  coverageDetails: domains.coverageDetails.data,
  coverageDetailsLoading: domains.coverageDetails.loading,
  coverageDetailsError: domains.coverageDetails.error,
  fetchCoverageDetails: domains.coverageDetails.runFetch,

  workflowConfig: domains.workflowConfig.data,
  workflowConfigLoading: domains.workflowConfig.loading,
  workflowConfigError: domains.workflowConfig.error,
  fetchWorkflowConfig: domains.workflowConfig.runFetch,

  reviewsData: domains.reviewsData.data,
  reviewsDataLoading: domains.reviewsData.loading,
  reviewsDataError: domains.reviewsData.error,
  fetchReviewsData: domains.reviewsData.runFetch,

  clinicalConditions: domains.clinicalConditions.data,
  clinicalConditionsLoading: domains.clinicalConditions.loading,
  clinicalConditionsError: domains.clinicalConditions.error,
  fetchClinicalConditions: domains.clinicalConditions.runFetch,

  authorizationData: domains.authorizationData.data,
  authorizationDataLoading: domains.authorizationData.loading,
  authorizationDataError: domains.authorizationData.error,
  fetchAuthorizationData: domains.authorizationData.runFetch,

  submissionData: domains.submissionData.data,
  submissionDataLoading: domains.submissionData.loading,
  submissionDataError: domains.submissionData.error,
  fetchSubmissionData: domains.submissionData.runFetch,

  siblingOrders: domains.siblingOrders.data,
  siblingOrdersLoading: domains.siblingOrders.loading,
  siblingOrdersError: domains.siblingOrders.error,
  fetchSiblingOrders: domains.siblingOrders.runFetch,

  denialQueryBucketData: denialQueryBucket.data,
  denialQueryBucketLoading: denialQueryBucket.loading,
  denialQueryBucketError: denialQueryBucket.error,
  fetchDenialQueryBucket: (payload: DenialQueryBucketFetchPayload) =>
    denialQueryBucket.runFetch(
      payload.order_id,
      getOrgIdForFetchExternalWorklist(),
    ),

  candidHistoryWriteback: candidHistoryWriteback.data,
  candidHistoryWritebackLoading: candidHistoryWriteback.loading,
  candidHistoryWritebackError: candidHistoryWriteback.error,
  fetchCandidHistoryWriteback: candidHistoryWriteback.runFetch,

  filteredSiblingOrders,
  recomputeFilteredSiblingOrders,
  loading,
  fetchAll,
  abortAll,
  resetAll,

  selectedSiblingOrderIds,
  setSelectedSiblingOrderIds,
  isSiblingOrderSelectionDisabled,
  setIsSiblingOrderSelectionDisabled,
});
